/*
 * WFC homepage concept « 5-6-7-8 » — shared behaviour for sections/wfc-home-*.liquid
 * - Scroll reveals: a section is only "armed" (content hidden until it enters the viewport)
 *   once this script runs, motion is allowed and IntersectionObserver exists. No JS = everything visible.
 * - Scroll scenes: [data-h-scene] elements receive --p (0 → 1) from their position in the viewport, written
 *   once per animation frame and only while they are on screen. Native scrolling is never altered.
 *   Children marked [data-h-step] get is-active / is-past from that progress (the count, the level line).
 *   The count section pins on wide screens (class is-pinned); its state is a pure function of the scroll position.
 * - Count-up: [data-h-count] figures count from 0 once; the real value stays in the DOM for assistive tech.
 * - Video facades: nothing is requested from YouTube until the visitor presses play.
 */
(function () {
  if (window.WFCHome) return;
  window.WFCHome = true;

  var reduce = window.matchMedia('(prefers-reduced-motion: reduce)');
  var pinQuery = window.matchMedia('(min-width: 990px) and (min-height: 600px)');
  var observer = null;
  var sceneObserver = null;
  var countObserver = null;
  var liveScenes = [];
  var allScenes = [];
  var frame = 0;

  function reveal(el) {
    el.classList.add('is-in');
  }

  function showAll(root) {
    root.querySelectorAll('[data-h-reveal]').forEach(reveal);
  }

  function getObserver() {
    if (observer) return observer;
    observer = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (!entry.isIntersecting) return;
          reveal(entry.target);
          observer.unobserve(entry.target);
        });
      },
      { rootMargin: '0px 0px -12% 0px', threshold: 0.12 }
    );
    return observer;
  }

  /* --- Scroll scenes ------------------------------------------------------ */

  function clamp01(n) {
    return n < 0 ? 0 : n > 1 ? 1 : n;
  }

  // exit: the element leaving the top · through: entering at the bottom → gone at the top
  // enter: its top travelling from the bottom of the viewport to 30% of it
  // read: its top crossing a reading line at 80% of the viewport · pin: a sticky track from top-at-top to bottom-at-bottom
  // list: the unpinned count (small screens), following a reading line at 60% of the viewport
  function progress(rect, mode, vh) {
    if (mode === 'exit') return clamp01(-rect.top / (rect.height || 1));
    if (mode === 'enter') return clamp01((vh - rect.top) / (vh * 0.7));
    if (mode === 'read') return clamp01((vh * 0.8 - rect.top) / Math.max(rect.height, vh * 0.4));
    if (mode === 'pin') return clamp01(-rect.top / Math.max(rect.height - vh, 1));
    if (mode === 'list') return clamp01((vh * 0.6 - rect.top) / (rect.height || 1));
    return clamp01((vh - rect.top) / (vh + rect.height));
  }

  function sceneMode(el) {
    var mode = el.getAttribute('data-h-scene');
    return mode === 'pin' && !el.wfcPinned ? 'list' : mode;
  }

  function activeStep(el, mode, p, vh) {
    var steps = el.wfcSteps;
    var n = steps.length;
    if (mode === 'pin') return Math.min(n - 1, Math.floor(p * n));
    if (mode === 'list') {
      var index = -1;
      for (var i = 0; i < n; i++) {
        if (steps[i].getBoundingClientRect().top < vh * 0.6) index = i;
      }
      return index;
    }
    return p <= 0 ? -1 : Math.min(n - 1, Math.floor(p * n));
  }

  function setSteps(list, active) {
    for (var i = 0; i < list.length; i++) {
      list[i].classList.toggle('is-active', i === active);
      list[i].classList.toggle('is-past', i < active);
    }
  }

  function update() {
    frame = 0;
    var vh = window.innerHeight;
    // Read everything first, then write, so the frame never forces a second layout.
    var results = liveScenes.map(function (el) {
      var mode = sceneMode(el);
      var p = progress(el.getBoundingClientRect(), mode, vh);
      return { el: el, p: p, step: el.wfcSteps.length ? activeStep(el, mode, p, vh) : null };
    });
    results.forEach(function (r) {
      var value = r.p.toFixed(4);
      if (r.el.wfcP !== value) {
        r.el.wfcP = value;
        r.el.style.setProperty('--p', value);
      }
      if (r.step !== null && r.step !== r.el.wfcStep) {
        r.el.wfcStep = r.step;
        setSteps(r.el.wfcSteps, r.step);
        setSteps(r.el.wfcLabels, r.step);
      }
    });
  }

  function requestUpdate() {
    if (!frame) frame = window.requestAnimationFrame(update);
  }

  function getSceneObserver() {
    if (sceneObserver) return sceneObserver;
    sceneObserver = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          var i = liveScenes.indexOf(entry.target);
          if (entry.isIntersecting && i === -1) liveScenes.push(entry.target);
          if (!entry.isIntersecting && i !== -1) liveScenes.splice(i, 1);
        });
        requestUpdate();
      },
      { rootMargin: '25% 0px 25% 0px' }
    );
    window.addEventListener('scroll', requestUpdate, { passive: true });
    window.addEventListener('resize', requestUpdate);
    return sceneObserver;
  }

  function applyPin(scene) {
    if (scene.getAttribute('data-h-scene') !== 'pin') return;
    var section = scene.closest('.wfc-home');
    var pinned = pinQuery.matches && !reduce.matches;
    scene.wfcPinned = pinned;
    scene.wfcStep = undefined;
    if (section) section.classList.toggle('is-pinned', pinned);
    // Pinned beats are driven by is-active / is-past, not by the one-shot reveal.
    if (pinned) scene.querySelectorAll('[data-h-reveal]').forEach(reveal);
  }

  function addScene(el) {
    if (el.wfcScene) return;
    el.wfcScene = true;
    el.wfcSteps = Array.prototype.slice.call(el.querySelectorAll('[data-h-step]'));
    el.wfcLabels = Array.prototype.slice.call(el.querySelectorAll('[data-h-step-label]'));
    allScenes.push(el);
    applyPin(el);
    getSceneObserver().observe(el);
  }

  function clearScenes() {
    allScenes.forEach(function (el) {
      if (sceneObserver) sceneObserver.unobserve(el);
      el.style.removeProperty('--p');
      el.wfcScene = false;
      el.wfcP = undefined;
      var section = el.closest('.wfc-home');
      if (section) section.classList.remove('is-pinned');
    });
    allScenes = [];
    liveScenes = [];
  }

  /* --- Count-up ----------------------------------------------------------- */

  var NUMBER = /\d[\d\s  ]*(?:[.,]\d+)?/;
  var SPACES = /[\s  ]/g;

  function countUp(el) {
    var text = el.textContent;
    var match = text.match(NUMBER);
    if (!match) return;
    var raw = match[0].replace(/[\s  ]+$/, '');
    var decimalPart = raw.match(/[.,](\d+)$/);
    var decimals = decimalPart ? decimalPart[1].length : 0;
    var decimalMark = decimalPart ? raw.charAt(raw.length - decimals - 1) : ',';
    var group = raw.match(/\d[\s  ]\d/) ? raw.match(/[\s  ]/)[0] : '';
    var target = parseFloat(raw.replace(SPACES, '').replace(',', '.'));
    if (!isFinite(target) || target <= 0) return;
    var before = text.slice(0, match.index);
    var after = text.slice(match.index + raw.length);

    function format(value) {
      var parts = value.toFixed(decimals).split('.');
      var whole = group ? parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, group) : parts[0];
      return before + whole + (decimals ? decimalMark + parts[1] : '') + after;
    }

    var spoken = document.createElement('span');
    spoken.className = 'visually-hidden';
    spoken.textContent = text;
    var shown = document.createElement('span');
    shown.setAttribute('aria-hidden', 'true');
    shown.textContent = format(0);
    el.textContent = '';
    el.appendChild(spoken);
    el.appendChild(shown);

    var start = null;
    var duration = 1400;
    function step(now) {
      if (start === null) start = now;
      var t = Math.min(1, (now - start) / duration);
      var eased = 1 - Math.pow(1 - t, 3);
      shown.textContent = t < 1 ? format(target * eased) : text;
      if (t < 1) window.requestAnimationFrame(step);
    }
    window.requestAnimationFrame(step);
  }

  function getCountObserver() {
    if (countObserver) return countObserver;
    countObserver = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (!entry.isIntersecting) return;
          countObserver.unobserve(entry.target);
          countUp(entry.target);
        });
      },
      { threshold: 0.6 }
    );
    return countObserver;
  }

  /* --- Arming ------------------------------------------------------------- */

  function arm(section) {
    if (section.dataset.hArmed) return;
    section.dataset.hArmed = 'true';

    var designMode = window.Shopify && window.Shopify.designMode;
    if (reduce.matches || designMode || !('IntersectionObserver' in window)) {
      showAll(section);
      return;
    }

    var targets = section.querySelectorAll('[data-h-reveal]');
    var fold = window.innerHeight * 0.9;
    // Anything already on screen is shown without a transition, so arming never flashes content.
    targets.forEach(function (el) {
      if (el.getBoundingClientRect().top < fold) {
        el.classList.add('is-in', 'is-instant');
      }
    });
    section.classList.add('wfc-h-motion');
    targets.forEach(function (el) {
      if (el.classList.contains('is-instant')) {
        requestAnimationFrame(function () {
          requestAnimationFrame(function () {
            el.classList.remove('is-instant');
          });
        });
      } else {
        getObserver().observe(el);
      }
    });

    if (section.matches('[data-h-scene]')) addScene(section);
    section.querySelectorAll('[data-h-scene]').forEach(addScene);
    section.querySelectorAll('[data-h-count]').forEach(function (el) {
      getCountObserver().observe(el);
    });
  }

  function playVideo(button) {
    var holder = button.closest('[data-h-video]');
    if (!holder) return;
    var id = holder.getAttribute('data-h-video');
    if (!/^[\w-]{6,20}$/.test(id)) return;
    var iframe = document.createElement('iframe');
    iframe.src = 'https://www.youtube-nocookie.com/embed/' + id + '?autoplay=1&rel=0&playsinline=1';
    iframe.title = holder.getAttribute('data-h-video-title') || 'Vidéo';
    iframe.allow = 'accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture';
    iframe.allowFullscreen = true;
    iframe.setAttribute('loading', 'lazy');
    button.replaceWith(iframe);
    iframe.focus();
  }

  function init(root) {
    (root || document).querySelectorAll('.wfc-home').forEach(arm);
  }

  document.addEventListener('click', function (event) {
    var button = event.target.closest && event.target.closest('[data-h-video-play]');
    if (button) {
      event.preventDefault();
      playVideo(button);
    }
  });

  var onReduceChange = function () {
    if (reduce.matches) {
      clearScenes();
      document.querySelectorAll('.wfc-home').forEach(showAll);
    }
  };
  if (reduce.addEventListener) reduce.addEventListener('change', onReduceChange);

  var onPinChange = function () {
    allScenes.forEach(applyPin);
    requestUpdate();
  };
  if (pinQuery.addEventListener) pinQuery.addEventListener('change', onPinChange);

  document.addEventListener('shopify:section:load', function (event) {
    init(event.target);
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      init();
    });
  } else {
    init();
  }
})();
